import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { Button, Modal, Result, Space } from "antd";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, type To } from "react-router";
import { ModalSizes } from "../../common/constants";
import type { FinancialSector } from "../../modules/agent/enums";
import { useCurrentAgentAccount, useIsCurrentAgentTypePlus } from "../../modules/auth/hooks";

const EMPTY_ELIGIBLE_SECTORS: FinancialSector[] = [];

interface CompetenceCheckOptions {
  sector: FinancialSector;
  redirectTo?: To;
  onBlockedClose?: VoidFunction;
}

type CompetenceCheckModalConfig = Omit<CompetenceCheckOptions, "sector"> & {
  onContinue?: VoidFunction;
};

interface CompetencePageCheckProps extends CompetenceCheckOptions {
  children?: ReactNode;
  enabled?: boolean;
}

interface CompetenceCheckProviderProps {
  children: ReactNode;
}

type CompetenceCheckContextType = {
  checkCompetence: (options: CompetenceCheckOptions, onContinue?: VoidFunction) => boolean;
  guardCompetenceAction: (options: CompetenceCheckOptions, callback: VoidFunction) => boolean;
};

const CompetenceCheckContext = createContext<CompetenceCheckContextType | undefined>(undefined);

export const CompetenceCheckProvider = ({ children }: CompetenceCheckProviderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Only Plus agents have their competences checked.
  const enabled = useIsCurrentAgentTypePlus();

  const agentUserAccount = useCurrentAgentAccount();
  const eligibleSectors = agentUserAccount?.agent.sectors ?? EMPTY_ELIGIBLE_SECTORS;

  const [blockedModalConfig, setBlockedModalConfig] = useState<CompetenceCheckModalConfig>();

  const openBlockedModal = useCallback((options: CompetenceCheckOptions, onContinue?: VoidFunction) => {
    setBlockedModalConfig({
      redirectTo: options.redirectTo,
      onBlockedClose: options.onBlockedClose,
      onContinue,
    });
  }, []);

  const checkCompetence = useCallback(
    (options: CompetenceCheckOptions, onContinue?: VoidFunction): boolean => {
      if (!enabled) {
        return true;
      }

      const isAllowed = eligibleSectors.includes(options.sector);

      if (!isAllowed) {
        openBlockedModal(options, onContinue);
      }

      return isAllowed;
    },
    [eligibleSectors, enabled, openBlockedModal],
  );

  const guardCompetenceAction = useCallback(
    (options: CompetenceCheckOptions, callback: VoidFunction): boolean => {
      if (!enabled) {
        callback();
        return true;
      }

      const isAllowed = eligibleSectors.includes(options.sector);

      if (!isAllowed) {
        openBlockedModal(options, callback);
        return false;
      }

      callback();
      return true;
    },
    [eligibleSectors, enabled, openBlockedModal],
  );

  useEffect(() => {
    if (!enabled) {
      setBlockedModalConfig(undefined);
    }
  }, [enabled]);

  const handleBlockedModalClose = () => {
    setBlockedModalConfig(undefined);
    blockedModalConfig?.onBlockedClose?.();

    if (blockedModalConfig?.redirectTo) {
      navigate(blockedModalConfig.redirectTo, { replace: true });
    }
  };

  const handleBlockedModalContinue = () => {
    setBlockedModalConfig(undefined);
    blockedModalConfig?.onContinue?.();
  };

  const contextValue = useMemo<CompetenceCheckContextType>(
    () => ({
      checkCompetence,
      guardCompetenceAction,
    }),
    [checkCompetence, guardCompetenceAction],
  );

  return (
    <CompetenceCheckContext value={contextValue}>
      {children}

      <Modal
        destroyOnHidden
        footer={
          <Space>
            <Button type="primary" onClick={handleBlockedModalClose}>
              {t("common.ok")}
            </Button>

            {agentUserAccount?.adminOrSystemAdmin && (
              <SecondaryButton type="primary" onClick={handleBlockedModalContinue}>
                {t("common.continue")}
              </SecondaryButton>
            )}
          </Space>
        }
        onCancel={handleBlockedModalClose}
        open={!!blockedModalConfig}
        title={t("common.status.warning")}
        width={ModalSizes.SMALL}
      >
        <Result status="warning" title={<span style={{ fontSize: 21 }}>{t("agent.helpers.agentOfvWarning")}</span>} />
      </Modal>
    </CompetenceCheckContext>
  );
};

export const useCompetenceCheck = () => {
  const context = use(CompetenceCheckContext);

  if (!context) {
    throw new Error("useCompetenceCheck must be used within a CompetenceCheckProvider");
  }

  return context;
};

interface CompetenceClickCheckProps extends CompetenceCheckOptions {
  children: (onClickCapture: MouseEventHandler<HTMLElement>) => ReactNode;
  enabled?: boolean;
}

export const CompetenceClickCheck = ({
  children,
  enabled = true,
  onBlockedClose,
  redirectTo,
  sector,
}: CompetenceClickCheckProps) => {
  const { checkCompetence } = useCompetenceCheck();

  const handleClickCapture = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      // The click replayed after the user chose to continue is dispatched by code, so it is not trusted
      if (!enabled || !event.nativeEvent.isTrusted) {
        return;
      }

      const clickTarget = event.currentTarget;
      // An allowed click goes on by itself; only a blocked one is replayed, after the user chooses to continue
      const isAllowed = checkCompetence({ onBlockedClose, redirectTo, sector }, () => clickTarget.click());

      if (!isAllowed) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [enabled, onBlockedClose, redirectTo, checkCompetence, sector],
  );

  return children(handleClickCapture);
};

const useCompetencePageCheck = ({ enabled = true, onBlockedClose, redirectTo, sector }: CompetencePageCheckProps) => {
  const { checkCompetence } = useCompetenceCheck();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    checkCompetence({ onBlockedClose, redirectTo, sector });
  }, [checkCompetence, enabled, onBlockedClose, redirectTo, sector]);
};

export const CompetencePageCheck = ({ children, ...props }: CompetencePageCheckProps) => {
  useCompetencePageCheck(props);

  return <>{children}</>;
};
